import z from 'zod'
import { createFileRoute } from '@tanstack/react-router'
import { COMPLIANCE_STAFF_ROLES } from '@workspace/utils'
import { ConflictsOfInterestPage } from '@/features/compliance-registers/conflicts'
import { requireActiveSubscription, requireRoles } from '@/lib/middleware'

const searchSchema = z.object({
  /** Opens the detail rail — what `coi_review_due` / `coi_control_due` link to. */
  conflict: z.string().optional(),
  /** `suggested` opens the detections view — what `coi_suggested_review` links to. */
  status: z.string().optional(),
  type: z.string().optional(),
  person: z.string().optional(),
  from: z.string().optional(),
  obligation: z.string().optional(),
})

/**
 * Every `/compliance-conflicts*` READ route is gated by the tighter
 * `requireComplianceStaffRead` (no `health_carer`, no `supported_employee`) —
 * mirror that list here so a support worker is bounced to /403 up front instead
 * of landing on a page whose data all 403s.
 *
 * Declaring a conflict is deliberately open to every member on the API, but it
 * happens through a dialog on this page, so the page itself stays staff-only.
 * A self-declaration surface for workers is separate work.
 *
 * Role-list reconciliation (this repo's "two lists that must agree" defect
 * class — see `.claude/rules/…`): this directory's six files used to each
 * re-declare their own byte-identical copy on the stated theory that hand-sync
 * across files with a JSDoc naming the API guard was safer than an import. It
 * wasn't forcing anything — a future edit to only some copies would have
 * reproduced exactly the drift this comment warned readers to avoid manually.
 * Now a single `@workspace/utils` source (`hub.tsx`, `findings.tsx`,
 * `partners.tsx`, `restrictive-practices/index.tsx` + `monthly-report.tsx`
 * import the same constant).
 */
export const Route = createFileRoute(
  '/_authenticated/_organization/compliance/conflicts-of-interest',
)({
  beforeLoad: async () => {
    await requireRoles([...COMPLIANCE_STAFF_ROLES])
    await requireActiveSubscription()
  },
  validateSearch: searchSchema,
  component: ConflictsOfInterestPage,
})
